import { reloadSafely } from "../app/versionCheck";
import { useState } from "react";

import { Menu, MenuItem, MenuLabel } from "../components/Menu";
import { IconButton, Segmented } from "../components/ui";
import { useUi } from "../state/ui";
import { leaveWith } from "../state/unsavedDrafts";
import { flushOrderQueues } from "../views/order/orderSaveQueue";
import { ENGLISH_READY, LANGS, type Lang, setLang, t, useLang } from "./i18n";
import { LangFlag } from "./LangFlag";

/** Durée du glissement du sélecteur (jeton `--gx-slide`, index.css) : la langue change une fois le geste fini. */
const LANG_SLIDE_MS = 260;

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Remontage de l'application dans la nouvelle langue en fondu enchaîné (transition de vue du navigateur) plutôt
 *  qu'en un éclair ; navigateur sans transition de vue ou mouvements réduits : changement direct. La capture de la
 *  nouvelle vue attend le rendu de React (tâche suivante). */
const crossFade = (update: () => Promise<void>): Promise<void> => {
  if (typeof document === "undefined" || !document.startViewTransition || reducedMotion()) return update();
  return document.startViewTransition(async () => {
    await update();
    await new Promise((resolve) => setTimeout(resolve, 0));
  }).updateCallbackDone;
};

/** Changer de langue reconstruit toute l'application (`main.tsx`) : une saisie non enregistrée fait d'abord poser
 *  la question commune « Abandonner les modifications ? » (audit de persistance M4). Le geste reste fluide (demande
 *  d'Alexis du 26/10/2026) : `show` fait d'abord glisser le sélecteur vers la langue choisie, puis l'interface
 *  passe en fondu dans la nouvelle langue. Dictionnaire introuvable (onglet ouvert pendant une mise en ligne, audit
 *  m14) : la langue reste celle d'avant (le sélecteur revient), « Recharger » est proposé. */
const switchTo = (current: Lang, next: Lang, show?: (lang: Lang) => void) => {
  if (next === current) return;
  leaveWith(() => {
    show?.(next);
    const slid = show && !reducedMotion() ? new Promise((resolve) => setTimeout(resolve, LANG_SLIDE_MS)) : Promise.resolve();
    void slid
      .then(() => crossFade(() => setLang(next)))
      .catch(() => {
        show?.(current);
        useUi.getState().notify({
          text: t("Langue non changée"),
          detail: t("Une nouvelle version de Generex est peut-être en ligne : rechargez la page, puis choisissez de nouveau."),
          tone: "warn",
          action: {
            label: t("Recharger"),
            run: () => leaveWith(() => void reloadSafely(() => flushOrderQueues(), () => window.location.reload())),
          },
        });
      });
  });
};

/** Choix de la langue de l'interface (FR · EN), retenu sur ce poste : `Segmented` compact (R8), comme le choix du
 *  modèle d'IA. `fill` : segments sur toute la largeur (dans un menu). Chaque segment porte le petit drapeau de sa
 *  langue (demande d'Alexis du 24/10/2026) : drapeau français, Union Jack pour l'anglais OTAN britannique ; celui
 *  de la langue active en couleurs pleines, l'autre en retrait. Nom accessible : « Français », « English ». */
export function LangSwitch({ className, fill = false, compact = false }: { className?: string; fill?: boolean | "natural"; compact?: boolean }) {
  const current = useLang();
  // Langue montrée par le sélecteur : la choisie dès l'appui (le fond glisse), la courante une fois l'application
  // remontée dans cette langue.
  const [shown, setShown] = useState(current);
  if (!ENGLISH_READY) return null;
  return (
    <Segmented<Lang>
      size="sm"
      fill={fill}
      slide
      ariaLabel={t("Langue de l'interface")}
      className={className}
      value={shown}
      onChange={(value) => {
        if (shown === current) switchTo(current, value, setShown);
      }}
      options={LANGS.map((option) => ({
        value: option.key,
        // `compact` (pied de la barre latérale, 260 px partagés avec le modèle d'IA et les réglages) : drapeau seul,
        // le sigle reste le nom accessible du segment.
        label: compact ? <span className="sr-only">{option.label}</span> : option.label,
        icon: <LangFlag lang={option.key} dimmed={option.key !== shown} />,
        title: option.name,
      }))}
    />
  );
}

/** Même choix en entrées de menu à choix exclusif (menu du rail) : parcourues aux flèches comme les autres entrées,
 *  là où un `Segmented` ne serait pas atteint au clavier. */
export function LangMenuItems() {
  const current = useLang();
  if (!ENGLISH_READY) return null;
  return (
    <>
      <MenuLabel>{t("Langue de l'interface")}</MenuLabel>
      {LANGS.map((option) => (
        <MenuItem
          key={option.key}
          icon={<LangFlag lang={option.key} dimmed={option.key !== current} />}
          label={option.name}
          selected={current === option.key}
          keepOpen
          onSelect={() => switchTo(current, option.key)}
        />
      ))}
    </>
  );
}

/** Langue active, à l'endroit le plus vu de l'espace de travail (bandeau du haut de la barre latérale, à côté du
 *  nom Generex) : son drapeau seul, nommé (« English »). Purement indicatif ; le choix se fait au pied de la barre. */
export function LangIndicator({ className, size }: { className?: string; size?: number }) {
  const current = useLang();
  if (!ENGLISH_READY) return null;
  const name = LANGS.find((option) => option.key === current)?.name ?? current;
  return (
    <LangFlag
      key={current}
      lang={current}
      size={size}
      label={t("Langue de l'interface : {langue}", { langue: name })}
      className={className}
    />
  );
}

/** Rail (barre repliée) : le drapeau de la langue active, qui ouvre le choix de la langue (entrées à choix
 *  exclusif, parcourues aux flèches). */
export function LangRailMenu() {
  const current = useLang();
  const [open, setOpen] = useState(false);
  if (!ENGLISH_READY) return null;
  const name = LANGS.find((option) => option.key === current)?.name ?? current;
  const label = t("Langue de l'interface : {langue}", { langue: name });
  return (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      portal
      side="right"
      align="end"
      width={200}
      ariaLabel={t("Langue de l'interface")}
      trigger={
        <IconButton size="sm" onClick={() => setOpen((o) => !o)} title={label} aria-label={label} aria-haspopup="menu" aria-expanded={open}>
          <LangFlag key={current} lang={current} />
        </IconButton>
      }
    >
      <LangMenuItems />
    </Menu>
  );
}
